<template>
  <form @submit.prevent="saveProperty" id="property-submit" class="property-form">
    <section class="property-card property-card--primary">
      <div class="property-card-heading">
        <span class="property-card-step">1</span>
        <div>
          <h3>Základné údaje</h3>
          <p>Údaje používané pri výpočte aj vo všetkých bankových žiadostiach.</p>
        </div>
      </div>

      <div class="property-grid">
        <div class="input-group">
          <label for="hodnota-nehnutelnosti" class="field-label">Odhadovaná hodnota</label>
          <div class="input-with-suffix">
            <input
              v-model.number="form.hodnota"
              type="number"
              min="0"
              step="0.01"
              class="text-input w-input required"
              name="hodnota-nehnutelnosti"
              id="hodnota-nehnutelnosti"
              required
            />
            <span>EUR</span>
          </div>
        </div>
        <div class="dropdown-input-custom rest-input" v-if="propertyTypeHelper === 'not_specified'">
          <dropdown-select
            @select="selectSubTypeOfProperty($event)"
            :id="'druh_nehnutelnosti'"
            :init-value="propertyTypes[0].value"
            :options="propertyTypes"
            normal
            :name="'Druh nehnuteľnosti'"
          />
        </div>
        <div class="dropdown-input-custom rest-input required" v-if="subTypeData !== 'not_specified'">
          <dropdown-select
            @select="setDropdownValue($event, 'pravny_stav')"
            :id="'property_pravny_stav'"
            :init-value="propertyTypeHelper === 'not_specified' ? null : form.pravny_stav"
            :value-removal="removePravnyStavValue"
            @value-removed="removePravnyStavValue = $event"
            :options="getAccordingOptions()"
            required
            normal
            :name="'Právny stav ' + type.label"
          />
        </div>
      </div>

      <div v-if="form.pravny_stav === 'developersky_projekt'" class="input-group property-full-width">
        <label for="developer-project" class="field-label">Názov developerského projektu</label>
        <input
          v-model.trim="form.developersky_projekt_nazov"
          type="text"
          class="text-input w-input"
          maxlength="256"
          id="developer-project"
        />
      </div>
    </section>

    <section class="property-card">
      <div class="property-card-heading">
        <span class="property-card-step">2</span>
        <div>
          <h3>Adresa a kataster</h3>
          <p>LV, parcela a katastrálne územie sa vypĺňajú aj pri byte a dome.</p>
        </div>
      </div>

      <sidlo-input class="property-address"
        @address-change="setAddress($event)"
        :google-place-id="googlePlaceId"
        :type="typeData"
        :not-required="druh === 'zakladane_nehnutelnosti' && typeData === 'land' ? {street: true} : null"
        :property-meta="propertyMeta"
        :not-built="form.pravny_stav === 'rozostavana'"
      />

      <div class="property-grid property-grid--three">
        <div class="input-group property-grid-span-2">
          <label for="cadastral-area" class="field-label">Katastrálne územie</label>
          <input
            v-model.trim="form.katastralne_uzemie"
            type="text"
            class="text-input w-input"
            maxlength="256"
            id="cadastral-area"
          />
        </div>
        <div class="dropdown-input-custom rest-input">
          <dropdown-select
            @select="setDropdownValue($event, 'kataster_register')"
            :id="'property_kataster_register'"
            :init-value="form.kataster_register"
            :options="katasterRegisters"
            normal
            :name="'Register parcely'"
          />
        </div>
      </div>
    </section>

    <section v-if="typeData !== 'land'" class="property-card">
      <div class="property-card-heading">
        <span class="property-card-step">3</span>
        <div>
          <h3>Parametre nehnuteľnosti</h3>
          <p>Tieto polia požaduje najmä Prima banka a ČSOB.</p>
        </div>
      </div>

      <div class="property-grid property-grid--three">
        <div v-if="typeData === 'flat'" class="input-group">
          <label for="apartment-number" class="field-label">Číslo bytu / priestoru</label>
          <input
            v-model.trim="form.cislo_priestoru"
            type="text"
            class="text-input w-input"
            maxlength="16"
            id="apartment-number"
          />
        </div>
        <div class="input-group">
          <label for="floor-area" class="field-label">Podlahová plocha</label>
          <div class="input-with-suffix">
            <input
              v-model.number="form.podlahova_plocha"
              type="number"
              min="0"
              step="0.01"
              class="text-input w-input"
              id="floor-area"
            />
            <span>m²</span>
          </div>
        </div>
        <div class="input-group">
          <label for="room-count" class="field-label">Počet izieb</label>
          <input
            v-model.number="form.pocet_izieb"
            type="number"
            min="0"
            step="1"
            class="text-input w-input"
            id="room-count"
          />
        </div>
        <div v-if="form.pravny_stav !== 'rozostavana'" class="input-group">
          <label for="occupancy-year" class="field-label">Rok kolaudácie</label>
          <input
            v-model.number="form.rok_kolaudacie"
            type="number"
            min="1000"
            max="9999"
            step="1"
            class="text-input w-input"
            id="occupancy-year"
          />
        </div>
        <div class="dropdown-input-custom rest-input">
          <dropdown-select
            @select="setDropdownValue($event, 'celorocne_byvanie')"
            :id="'property_year_round_housing'"
            :init-value="form.celorocne_byvanie"
            :options="booleanChoices"
            normal
            :name="'Celoročné bývanie'"
          />
        </div>
      </div>
    </section>

    <section class="property-card">
      <div class="property-card-heading">
        <span class="property-card-step">{{ typeData === 'land' ? 3 : 4 }}</span>
        <div>
          <h3>Vlastníctvo</h3>
          <p>Podiel a titul nadobudnutia pomôžu pri záložnej časti žiadosti.</p>
        </div>
      </div>

      <div class="property-grid">
        <div class="dropdown-input-custom rest-input">
          <dropdown-select
            @select="changeOwner($event)"
            :id="'property_typ_vlastnictva'"
            :init-value="translateOwnershipFromDbValue()"
            :options="getOwnerChoices()"
            normal
            :name="'Vlastník'"
          />
        </div>
        <div class="input-group">
          <label for="owner-share" class="field-label">Podiel vlastníka</label>
          <div class="input-with-suffix">
            <input
              v-model.number="form.spoluvlastnicky_podiel_vlastnika"
              type="number"
              min="0"
              max="100"
              step="0.01"
              class="text-input w-input"
              id="owner-share"
            />
            <span>%</span>
          </div>
        </div>
        <div class="dropdown-input-custom rest-input">
          <dropdown-select
            @select="setDropdownValue($event, 'titul_nadobudnutia')"
            :id="'property_acquisition_title'"
            :init-value="form.titul_nadobudnutia"
            :options="acquisitionTitles"
            normal
            :name="'Titul nadobudnutia'"
          />
        </div>
        <div class="input-group">
          <label for="acquisition-date" class="field-label">Dátum nadobudnutia</label>
          <input
            v-model="form.datum_nadobudnutia"
            type="date"
            class="text-input w-input"
            id="acquisition-date"
          />
        </div>
      </div>

      <div v-if="ownerHelper === 'other'" class="property-related-person">
        <client-picker
          @client-picked="setClient($event)"
          @busy="clientBusy = $event"
          :client-init="otherOwnerId"
          complex
        />
        <p v-if="clientBusy" class="light-text">Pred uložením nehnuteľnosti dokončite údaje klienta alebo sa vráťte k výberu.</p>
      </div>
    </section>

    <section class="property-card property-card--optional">
      <button type="button" class="property-card-toggle" @click="advancedOpen = !advancedOpen">
        <span>
          <strong>Ťarchy a vecné bremená</strong>
          <small>Voliteľné, ale dôležité pri refinancovaní alebo ďalšom poradí záložného práva.</small>
        </span>
        <span class="property-card-chevron" :class="{ open: advancedOpen }">⌄</span>
      </button>

      <v-expand-transition>
        <div v-show="advancedOpen" class="property-advanced-content">
          <div class="property-grid">
            <div class="dropdown-input-custom rest-input">
              <dropdown-select
                @select="setDropdownValue($event, 'tarcha_banka')"
                :id="'property_tarcha_banky'"
                :init-value="form.tarcha_banka"
                :options="banks"
                normal
                :name="'Banka existujúcej ťarchy'"
              />
            </div>
            <div class="input-group">
              <label for="orient-zost" class="field-label">Orientačný zostatok</label>
              <div class="input-with-suffix">
                <input
                  v-model.number="form.tarcha_orientacny_zostatok"
                  type="number"
                  min="0"
                  step="0.01"
                  class="text-input w-input"
                  id="orient-zost"
                />
                <span>EUR</span>
              </div>
            </div>
          </div>
          <div class="input-group property-full-width">
            <label for="easements" class="field-label">Vecné bremená</label>
            <textarea
              v-model.trim="form.vecne_bremena"
              class="text-input w-input property-textarea"
              maxlength="2000"
              id="easements"
              placeholder="Napr. právo doživotného bývania; nechajte prázdne, ak sa na LV nenachádza."
            ></textarea>
          </div>
        </div>
      </v-expand-transition>
    </section>
  </form>
</template>

<script>
import DropdownSelect from "../../../DropdownSelect";
import { mapGetters } from "vuex";
import SidloInput from "../../../SidloInput";
import ClientPicker from "../ClientPicker/ClientPicker";
import propertyFilters from "../../../../../mixins/propertyFilters";

export default {
  name: "PropertyForm",
  components: {ClientPicker, SidloInput, DropdownSelect},
  props: {
    druh: String,
    property: [Object, null],
    type: [Object, null],
    formTemplate: Object,
    banks: [Array, Object],
  },
  computed: {
    ...mapGetters({
      mortgageGetter: "mortgageDetailState/getMortgage",
    }),
  },
  watch: {
    mortgageGetter (newMortgage) {
      this.mortgage = newMortgage;
    },
  },
  mixins: [
    propertyFilters,
  ],
  created () {
    this.applicantId = this.$store.getters["mortgageDetailState/getApplicant"].id;
    this.coApplicantId = this.$store.getters["mortgageDetailState/getCoApplicant"]?.id;
    this.typeData = this.type.type;
    this.subTypeData = this.type.subtype;
    this.mortgage = this.$store.getters["mortgageDetailState/getMortgage"];
    if (this.property) {
      this.typeData = this.property.child_type ? this.property.child_type : "not_specified";
      this.propertyTypeHelper = this.property.child_type ? this.property.child_type : "not_specified";
      this.form.hodnota = this.property.hodnota;
      this.form.pravny_stav = this.property.pravny_stav;
      this.form.majitel = this.property.majitel;
      this.form.tarcha_banka = this.property.tarcha_banka;
      this.form.tarcha_orientacny_zostatok =
        this.property.tarcha_orientacny_zostatok;
      this.form.katastralne_uzemie = this.property.katastralne_uzemie;
      this.form.kataster_register = this.property.kataster_register;
      this.form.podlahova_plocha = this.property.podlahova_plocha;
      this.form.pocet_izieb = this.property.pocet_izieb;
      this.form.rok_kolaudacie = this.property.rok_kolaudacie;
      this.form.developersky_projekt_nazov = this.property.developersky_projekt_nazov;
      this.form.celorocne_byvanie = this.property.celorocne_byvanie;
      this.form.cislo_priestoru = this.property.cislo_priestoru;
      this.form.spoluvlastnicky_podiel_vlastnika = this.property.spoluvlastnicky_podiel_vlastnika;
      this.form.titul_nadobudnutia = this.property.titul_nadobudnutia;
      this.form.datum_nadobudnutia = this.property.datum_nadobudnutia;
      this.form.vecne_bremena = this.property.vecne_bremena;
      this.googlePlaceId = this.property.google_place_id;
      this.propertyMeta.cislo_lv = this.property.cislo_lv;
      this.propertyMeta.cislo_parcely = this.property.cislo_parcely;
      this.propertyMeta.address_text_number = this.property.address_text_number;
      this.propertyMeta.address_text_popisne = this.property.address_text_popisne;
      this.propertyMeta.address_text_county = this.property.address_text_county;
      this.propertyMeta.address_text_district = this.property.address_text_district;
      this.propertyMeta.address_text_city = this.property.address_text_city;
      this.propertyMeta.address_text_street = this.property.address_text_street;
      this.propertyMeta.address_text_zip = this.property.address_text_zip;
      this.propertyMeta.address_text_country = this.property.address_text_country;
      this.form.meta = this.property.meta;
      this.advancedOpen = Boolean(
        this.form.tarcha_banka
        || this.form.tarcha_orientacny_zostatok
        || this.form.vecne_bremena
      );
    }
    if (this.applicantId !== this.form.majitel && this.coApplicantId !== this.form.majitel) {
      this.otherOwnerId = this.form.majitel;
      this.otherOwner = null;
    }
    this.ownerHelper = this.translateOwnershipFromDbValue();
  },
  methods: {
    selectSubTypeOfProperty (subType) {
      this.subTypeData = subType;
      this.typeData = this.getPropertyDataBySubType(subType).type;
      this.removePravnyStavValue = true;
    },
    getAccordingOptions () {
      return this[this.subTypeData];
    },
    setClient (client) {
      this.otherOwner = client;
      this.otherOwnerId = client?.id || null;
    },
    getOwnerChoices () {
      if (this.coApplicantId)
        return [
          {
            display_name: "žiadateľ",
            value: "applicant",
          },
          {
            display_name: "spolužiadateľ",
            value: "coapplicant",
          },
          {
            display_name: "tretia strana",
            value: "other",
          },
        ]
      return [
        {
          display_name: "žiadateľ",
          value: "applicant",
        },
        {
          display_name: "tretia strana",
          value: "other",
        },
      ]
    },
    setAddress (addressObject) {
      this.address = addressObject;
    },
    changeOwner (owner) {
      if (owner !== "other") {
        this.otherOwner = null;
      }
      this.ownerHelper = owner;
    },
    getOwner () {
      const owner = this.ownerHelper;
      if (owner === "applicant") return this.applicantId;
      else if (owner === "coapplicant") return this.coApplicantId;
      else if (owner === "other") return this.otherOwner?.id || this.otherOwnerId || null;
      return null;
    },
    translateOwnershipFromDbValue () {
      const owner = this.form.majitel;
      if (owner) {
        if (this.applicantId === owner) {
          return "applicant";
        } else if (this.coApplicantId === owner) {
          return "coapplicant";
        }
      }
      return "other";
    },
    setDropdownValue (value, dropdownName) {
      this.form[dropdownName] = value;
      if (dropdownName === "vydavky") {
        this.showEstimatedDtiToUser();
      }
    },
    convertOwner () {
      if (this.ownerHelper === "coapplicant") {
        return "coApplicant"
      }
      return this.ownerHelper;
    },
    saveProperty () {
      if (this.clientBusy) return;
      const propertyObject = {
        meta: {
          childTypeAlreadySpecified: this.propertyTypeHelper !== "not_specified",
          childType: this.getPropertyDataBySubType(this.subTypeData).type,
        },
        childData: this.typeData === "flat"
          ? {cislo_priestoru: this.form.cislo_priestoru || null}
          : {},
        data: {
          majitelType: this.convertOwner(),
          hodnota: this.form.hodnota,
          pravny_stav: this.form.pravny_stav,
          majitel: this.getOwner(),
          katastralne_uzemie: this.form.katastralne_uzemie || null,
          kataster_register: this.form.kataster_register || null,
          podlahova_plocha: this.form.podlahova_plocha || null,
          pocet_izieb: this.form.pocet_izieb || null,
          rok_kolaudacie: this.form.rok_kolaudacie || null,
          developersky_projekt_nazov: this.form.developersky_projekt_nazov || null,
          celorocne_byvanie: this.form.celorocne_byvanie,
          spoluvlastnicky_podiel_vlastnika: this.form.spoluvlastnicky_podiel_vlastnika || null,
          titul_nadobudnutia: this.form.titul_nadobudnutia || null,
          datum_nadobudnutia: this.form.datum_nadobudnutia || null,
          vecne_bremena: this.form.vecne_bremena || null,
          tarcha_banka: this.form.tarcha_banka,
          tarcha_orientacny_zostatok: this.form.tarcha_orientacny_zostatok,
          meta: this.form.meta,
          ...this.address,
        },
      };
      if (this.property) {
        propertyObject.id = this.property.id;
      }
      // console.log(this.form);
      // console.log(propertyObject);
      this.$emit("property-saved", propertyObject);
    },
  },
  data: () => ({
    applicantId: null,
    coApplicantId: null,
    address: null,
    typeData: null,
    subTypeData: null,
    propertyTypeHelper: null,
    removePravnyStavValue: false,
    propertyMeta: {
      cislo_lv: null,
      cislo_parcely: null,
      address_text_country: null,
      address_text_number: null,
      address_text_popisne: null,
      address_text_county: null,
      address_text_district: null,
      address_text_city: null,
      address_text_street: null,
      address_text_zip: null
    },
    form: {
      hodnota: null,
      pravny_stav: null,
      typ_vlastnictva: null,
      majitel: null,
      majitel_email: null,
      majitel_fisrt_name: null,
      majitel_last_name: null,
      majitel_telefon: null,
      tarcha_banka: null,
      tarcha_orientacny_zostatok: null,
      katastralne_uzemie: null,
      kataster_register: null,
      podlahova_plocha: null,
      pocet_izieb: null,
      rok_kolaudacie: null,
      developersky_projekt_nazov: null,
      celorocne_byvanie: null,
      cislo_priestoru: null,
      spoluvlastnicky_podiel_vlastnika: null,
      titul_nadobudnutia: null,
      datum_nadobudnutia: null,
      vecne_bremena: null,
      meta: null,
    },
    advancedOpen: false,
    otherOwner: null,
    otherOwnerId: null,
    clientBusy: false,
    googlePlaceId: null,
    ownerHelper: null,
    mortgage: null,
    katasterRegisters: [
      {display_name: "Register C", value: "C"},
      {display_name: "Register E", value: "E"},
    ],
    booleanChoices: [
      {display_name: "Áno", value: true},
      {display_name: "Nie", value: false},
    ],
    acquisitionTitles: [
      {display_name: "Kúpa", value: "kupa"},
      {display_name: "Dedenie", value: "osvedcenie_o_dedicstve"},
      {display_name: "Darovanie", value: "darovanie"},
      {display_name: "Výstavba", value: "vystavba"},
    ],
    propertyTypes: [
      {
        display_name: "Rodinný dom / chata",
        value: "house",
      },
      {
        display_name: "Byt / Apartmán",
        value: "flatInFlat",
      },
      {
        display_name: "Byt v rodinnom dome",
        value: "flatInHouse",
      },
      {
        display_name: "Pozemok",
        value: "land",
      },
    ],
    flatInHouse: [
      {
        display_name: "skolaudovaný",
        value: "skolaudovana",
      },
      {
        display_name: "rozostavaný",
        value: "rozostavana",
      }
    ],
    flatInFlat: [
      {
        display_name: "skolaudovaný",
        value: "skolaudovana",
      },
      {
        display_name: "developerský projekt",
        value: "developersky_projekt",
      }
    ],
    house: [
      {
        display_name: "skolaudovaný",
        value: "skolaudovana",
      },
      {
        display_name: "rozostavaný",
        value: "rozostavana",
      }
    ],
    land: [
      {
        display_name: "stavebny pozemok",
        value: "stavebny_pozemok",
      },
      {
        display_name: "pozemok bez stavebka",
        value: "pozemok",
      }
    ]
  }),
};
</script>

<style lang="scss" scoped>
.property-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 24px 32px;
}

.property-card {
  padding: 18px;
  border: 1px solid var(--app-border);
  border-radius: 16px;
  background: var(--app-surface);
  box-shadow: 0 6px 20px rgba(20, 42, 74, 0.045);
}

.property-card--primary {
  border-color: var(--app-border);
  background: var(--app-surface);
}

.property-card-heading {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-bottom: 16px;

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: var(--app-text);
    font-size: 14px;
    font-weight: 800;
  }

  p {
    margin-top: 3px;
    color: var(--app-text-muted);
    font-size: 11px;
    line-height: 1.45;
  }
}

.property-card-step {
  display: grid;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 9px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 900;
}

.property-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 14px;
}

.property-grid--three {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.property-grid-span-2 {
  grid-column: span 2;
}

.property-full-width {
  margin-top: 12px;
}

.input-with-suffix {
  position: relative;

  input {
    padding-right: 45px;
  }

  span {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    color: var(--app-text-muted);
    font-size: 10px;
    font-weight: 800;
    pointer-events: none;
  }
}

.property-related-person {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed var(--app-border);
}

.property-card--optional {
  padding: 0;
  overflow: hidden;
}

.property-card-toggle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 0;
  background: transparent;
  color: var(--app-text);
  text-align: left;
  cursor: pointer;

  strong,
  small {
    display: block;
  }

  strong {
    font-size: 13px;
    font-weight: 800;
  }

  small {
    margin-top: 3px;
    color: var(--app-text-muted);
    font-size: 10px;
    line-height: 1.4;
  }
}

.property-card-chevron {
  color: var(--app-primary);
  font-size: 22px;
  transition: transform 180ms ease;

  &.open {
    transform: rotate(180deg);
  }
}

.property-advanced-content {
  padding: 0 18px 18px;
  border-top: 1px solid var(--app-border);
  padding-top: 16px;
}

.property-textarea {
  min-height: 78px;
  padding-top: 9px;
  resize: vertical;
}

@media screen and (max-width: 480px) {
  .property-form {
    padding: 12px;
  }

  .property-grid,
  .property-grid--three {
    grid-template-columns: 1fr;
  }

  .property-grid-span-2 {
    grid-column: auto;
  }
}

.property-form ::v-deep .input-group { position:relative; display:block; width:100%; margin:0; padding:0; min-width:0; }
.property-form ::v-deep .field-label { position:static!important; display:block; width:auto; margin:0 0 6px!important; padding:0; transform:none; color:var(--app-text-muted)!important; font-size:11px!important; font-weight:500; line-height:1.4; }
.property-form ::v-deep input.text-input, .property-form ::v-deep textarea.text-input { display:block; width:100%!important; height:44px; min-height:44px; margin:0!important; padding:10px 12px!important; border:1px solid var(--app-border)!important; border-radius:9px!important; background:var(--app-surface-muted)!important; color:var(--app-text)!important; font:inherit; font-size:13px!important; box-sizing:border-box; box-shadow:none; }
.property-form ::v-deep .input-with-suffix input.text-input { padding-right:45px!important; }
.property-form ::v-deep .dropdown-input-custom { display:block!important; width:100%; min-width:0; margin:0; padding:0; border:0!important; background:transparent!important; }
.property-form ::v-deep .dropdown-input-custom>.wrapper { display:block!important; width:100%; min-width:0; }
.property-form ::v-deep .select-wrapper-normal { width:100%; height:44px; min-height:44px; padding:0!important; margin:0!important; border:1px solid var(--app-border)!important; border-radius:9px; background:var(--app-surface-muted)!important; box-shadow:none; }
.property-form ::v-deep .vs__dropdown-toggle { height:42px; padding:0 9px; border:0!important; background:transparent!important; border-radius:9px; }
.property-form ::v-deep .vs__search { min-width:0; margin:0; padding:0; height:auto; color:var(--app-text); font-size:13px; }
.property-form ::v-deep .vs__selected { margin:0; padding:0; font-size:13px; color:var(--app-text); }
.property-form ::v-deep .vs__dropdown-menu { color:var(--app-text); background:var(--app-surface); border-color:var(--app-border); }
.property-form ::v-deep .property-address { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px 14px; margin-bottom:12px; }
.property-form ::v-deep .property-address>div:not(.input-group), .property-form ::v-deep .property-address>div>div:not(.input-group) { display:contents; }
.property-form ::v-deep .property-address>.input-group { grid-column:1/-1; }
.property-form ::v-deep input:focus-visible, .property-form ::v-deep textarea:focus-visible, .property-form ::v-deep .vs--open .vs__dropdown-toggle { outline:2px solid var(--app-primary); outline-offset:2px; }
.property-form ::v-deep .input-with-suffix { width:100%; }
.property-form ::v-deep textarea.property-textarea { height:auto; min-height:86px; }
@media(max-width:480px) { .property-grid,.property-grid--three { grid-template-columns:1fr; }.property-form ::v-deep .property-address { grid-template-columns:1fr; } }
</style>
